import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

type TParams = URLSearchParams | Record<string, string>

interface IWizardHistoryOptions {
    /** the step on screen right now, as read from `?step=` */
    step: number
    /**
     * The query string for a step. Defaults to "every parameter already on
     * the URL, with `step` replaced" — the request wizard carries `?poolship=`
     * through every step that way. The grant wizard passes `{ step }` alone,
     * because its one-shot `?borrower=` / `?nudge=` link parameters must NOT
     * survive past the step they were applied on.
     */
    paramsFor?: (next: number, current: URLSearchParams) => TParams
}

/**
 * The wizard's steps as a real, LINEAR stack of history entries.
 *
 * Why this exists: the phone's back button and the wizard's own arrow have to
 * agree, and for two releases they did not.
 *
 * - First the arrow was `navigate(-1)`. The receipt's «ویرایش» lines push an
 *   entry too, so the stack became a log of every step VISITED, and after one
 *   jump back to edit, the arrow walked forward (93d4e8d, 47 rage-clicks).
 * - Then the arrow was made to push «step − 1». That fixed the arrow and broke
 *   the phone: 3, arrow → 2 is now [1, 2, 3, 2], so the OS back button goes
 *   to 3 again, then 2, then 3 — «می‌ره ۲ و باز ۳ و باز ۲» (owner, 2026-09-11,
 *   on the loan-request wizard).
 *
 * The fix is to never let the stack stop being «1, 2, …, current». Every entry
 * a step pushes records `wizardDepth`: how many of this wizard's own steps sit
 * directly beneath it, in order. Then:
 *
 * - forward pushes, one deeper;
 * - going back to an earlier step POPS (`navigate(-distance)`) whenever those
 *   entries are really there — so the arrow and the OS button are literally
 *   the same operation, and a receipt jump from 4 to 2 lands where two OS
 *   backs would have;
 * - when they are not there (the wizard was opened on a later step by a link,
 *   or a borrower was preselected with `replace`), it pops what IS there and
 *   then REPLACES, instead of pushing a step the phone would later walk back
 *   through;
 * - leaving from step 1 pops past everything the wizard owns.
 *
 * `history.state` survives a reload, so a wizard reloaded on step 3 still
 * knows its depth.
 */
export default function useWizardHistory({
    step,
    paramsFor,
}: IWizardHistoryOptions) {
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const depth =
        Number(
            (location.state as { wizardDepth?: number } | null)?.wizardDepth
        ) || 0

    const build = (next: number): TParams => {
        if (paramsFor) return paramsFor(next, searchParams)
        const params = new URLSearchParams(searchParams)
        params.set('step', String(next))
        return params
    }

    const write = (next: number, replace: boolean, nextDepth: number) => {
        setSearchParams(build(next), {
            replace,
            state: { wizardDepth: nextDepth },
        })
    }

    /**
     * A back-jump that needed more entries than there were: we popped to the
     * bottom of what this wizard owns, and still have to land on `target`.
     * Finished once the pop has actually happened, i.e. on the next location.
     */
    const pendingTarget = useRef<number | null>(null)
    useEffect(() => {
        const target = pendingTarget.current
        if (target === null) return
        pendingTarget.current = null
        if (step !== target) write(target, true, 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key])

    /** «ادامه» — one step on, one entry deeper. */
    const forward = (next: number) => write(next, false, depth + 1)

    /** Any move to an earlier step: the arrow, or a receipt «ویرایش» line. */
    const backTo = (target: number) => {
        const distance = step - target
        if (distance <= 0) return
        if (depth >= distance) {
            navigate(-distance)
            return
        }
        if (depth > 0) {
            pendingTarget.current = target
            navigate(-depth)
            return
        }
        write(target, true, 0)
    }

    /** Leave the wizard for wherever the user came from. */
    const exit = () => navigate(-(depth + 1))

    /**
     * Put a different step on the CURRENT entry — for moves the user did not
     * make themselves (a preselected borrower, a guard bouncing an empty
     * draft to step 1). The entry below is not this wizard's step − 1 any
     * more, so the depth restarts.
     */
    const replace = (next: number) => write(next, true, 0)

    const back = () => (step > 1 ? backTo(step - 1) : exit())

    return { forward, backTo, back, exit, replace, depth }
}
